import React, { useState, useCallback } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  startOfWeek,
  endOfWeek,
  isBefore,
  startOfDay,
} from "date-fns";
import clsx from "clsx";
import { LanguageCode } from "../api/contracts/language-settings";
import { BookingAvailability } from "../../../api-contracts/sites/wbe/booking-availability";
import {
  isStopTimeExpired,
  getStopTimeMessageForDate,
  getFirstBlockedStayDateByStopTime,
  isStayRangeBlockedByStopTime,
  isDateUnavailable,
  isDepartureDateDisabled,
} from "../utils/bookingAvailabilityUtils";
import { applyActivityRangeSelect, applyRangeSelect } from "../utils/dateRangeUtils";
import { Skeleton } from "./Skeleton";
import { getDateButtonClasses } from "./calendarDateClasses";

export interface CalendarLabels {
  weekdays: [string, string, string, string, string, string, string];
  arrival: string;
  departure: string;
  stopTimeStayDateNotice: string;
  loadingAvailability: string;
}

interface CalendarProps {
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  onDateSelect: (date: Date) => void;
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  variant?: "single" | "double";
  bookingAvailability?: BookingAvailability;
  noShadow?: boolean;
  nested?: boolean;
  /** Drop the calendar's own card chrome — border, rounding, padding and
   *  shadow — for a host that already draws one around it. Without this the
   *  booking flow renders a bordered card inside a bordered card. */
  bare?: boolean;
  requireRange?: boolean;
  onRangeChange?: (from: Date, to: Date | null) => void;
  /**
   * Activity-site selection: the first click selects a complete single day
   * (to = from) and a later click widens it into a range. Only meaningful
   * together with requireRange/onRangeChange.
   */
  singleDaySelect?: boolean;
  labels: CalendarLabels;
  languageCode: LanguageCode;
  /**
   * No usable availability for the month(s) on screen — nothing has arrived
   * yet, or what we hold belongs to a different window. Day buttons give way
   * to placeholder cells, because an empty feed means opposite things to the
   * two callers: per-category availability renders every date as blocked,
   * package availability renders every date as open.
   */
  isLoading?: boolean;
  /**
   * Refetching availability we already have for this window. The grid stays
   * live — replacing correct days with placeholders would only add flicker —
   * and a thin bar carries the "not settled yet" signal instead.
   */
  isRefreshing?: boolean;
}

// Box geometry of one day cell, shared by the real button and its loading
// placeholder. The -1px pull-up collapses the 1px borders between neighbouring
// cells, and it has to apply to the placeholder too: without it every row is
// 40px instead of 39px and the grid shrinks by a row-count of pixels the moment
// availability lands.
const DAY_CELL_BOX =
  "relative -ml-px -mt-px flex h-10 w-full items-center justify-center border border-transparent";

const formatMonthHeader = (date: Date, languageCode: LanguageCode) =>
  new Intl.DateTimeFormat(languageCode, {
    month: "long",
    year: "numeric",
  }).format(date);

const MonthCalendar: React.FC<{
  month: Date;
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  onDateSelect: (date: Date) => void;
  weekDays: string[];
  showHeader?: boolean;
  bookingAvailability?: BookingAvailability;
  requireRange?: boolean;
  onRangeChange?: (from: Date, to: Date | null) => void;
  singleDaySelect?: boolean;
  hoveredDate: Date | null;
  onDateHover: (date: Date | null) => void;
  languageCode: LanguageCode;
  isLoading?: boolean;
}> = ({
  month,
  selectedStartDate,
  selectedEndDate,
  onDateSelect,
  weekDays,
  showHeader = true,
  bookingAvailability,
  requireRange,
  onRangeChange,
  singleDaySelect,
  hoveredDate,
  onDateHover,
  languageCode,
  isLoading = false,
}) => {

  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  const isInRange = (date: Date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isStartOrEnd = (date: Date) => {
    return (
      (selectedStartDate && isSameDay(date, selectedStartDate)) ||
      (selectedEndDate && isSameDay(date, selectedEndDate))
    );
  };

  const isInDepartureMode = !!selectedStartDate && !selectedEndDate;

  const isDisabledForDeparture = (date: Date): boolean => {
    if (!selectedStartDate) return false;
    if (selectedEndDate) return false;
    return isDepartureDateDisabled(date, selectedStartDate, bookingAvailability);
  };

  const monthHeader = showHeader ? (
    <div className="mb-4 text-center">
      <h3
        className="text-base font-semibold capitalize text-ink"
        data-testid="calendar-month-label"
      >
        {formatMonthHeader(month, languageCode)}
      </h3>
    </div>
  ) : null;

  const weekdayHeaders = weekDays.map((day) => (
    <div
      key={day}
      className="flex h-10 items-center justify-center text-sm font-medium text-ink-secondary"
      data-testid="weekday-header"
    >
      {day}
    </div>
  ));

  if (isLoading) {
    // Same cell count as the real grid, so the calendar keeps its height and
    // nothing below it jumps when the answer lands.
    return (
      <div className="flex-1">
        {monthHeader}

        <div
          className="grid grid-cols-7 gap-y-1"
          data-testid="calendar-grid"
          aria-busy="true"
        >
          {weekdayHeaders}

          {calendarDays.map((_, dayIdx) => (
            <div key={`skeleton-${dayIdx}`} className="relative">
              <div
                className={DAY_CELL_BOX}
                data-testid="calendar-day-skeleton"
              >
                {/* `!` because Skeleton carries its own `rounded-md`, which otherwise
                    wins and leaves square placeholders. */}
                <Skeleton className="h-8 w-8 !rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1">
      {monthHeader}

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-y-1" data-testid="calendar-grid">
        {weekdayHeaders}

        {/* Calendar days */}
        {calendarDays.map((date, dayIdx) => {
          const isPastDate = isBefore(date, startOfDay(new Date()));
          const isUnavailable = isDateUnavailable(date, bookingAvailability);
          const stopTimeExpiredOnDate = isStopTimeExpired(
            date,
            bookingAvailability,
          );
          // Dates before arrival aren't blocked in departure mode — clicking resets
          // to a new arrival. Only dates past an unavailable block disable the button.
          const disabledForDeparture =
            isDisabledForDeparture(date) &&
            !(
              isInDepartureMode &&
              !!selectedStartDate &&
              (isBefore(date, selectedStartDate) || isSameDay(date, selectedStartDate))
            );
          const stayRangeBlockedByStopTime =
            !!selectedStartDate &&
            !selectedEndDate &&
            !isSameDay(date, selectedStartDate) &&
            !isBefore(date, selectedStartDate) &&
            isStayRangeBlockedByStopTime(
              selectedStartDate,
              date,
              bookingAvailability,
            );

          const stopTimeBlocked =
            stopTimeExpiredOnDate || stayRangeBlockedByStopTime;

          // When picking a departure date the date itself doesn't have to be
          // bookable — guests don't occupy the room on check-out day, only the
          // nights leading up to it. `disabledForDeparture` already enforces
          // that every stayed-on night is bookable, so `isUnavailable` would
          // only double-block the legitimate departure case (arrival + 1 when
          // that day is unavailable; the night before it is what matters).
          // Exception: dates BEFORE the selected arrival are shown as clickable
          // to reset to a new arrival — but they must still be blocked if they
          // are arrival-restricted or unavailable, otherwise a user can bypass
          // the arrival window by picking a valid arrival then clicking back.
          // Expired stop-time dates stay clickable so users can read the
          // stop-time guidance message shown below the calendar.
          // Actual booking is still blocked during search submission.
          const wouldResetToRestrictedArrival =
            isInDepartureMode &&
            !!selectedStartDate &&
            isBefore(date, selectedStartDate) &&
            isUnavailable;
          const isDisabled =
            isPastDate ||
            (!isInDepartureMode && isUnavailable) ||
            wouldResetToRestrictedArrival ||
            stayRangeBlockedByStopTime ||
            disabledForDeparture;

          const isNotSelected = !isStartOrEnd(date) && !isInRange(date);
          const isSelectable = !isDisabled;
          const isHovered =
            !!hoveredDate && isSameDay(date, hoveredDate) && isSelectable && isNotSelected;
          const isSingleDaySelected =
            !!selectedStartDate &&
            !!selectedEndDate &&
            isSameDay(selectedStartDate, selectedEndDate);
          const hoveredShape = isHovered
            ? singleDaySelect
              ? // A click either widens the selected single day (right cap of
                // the would-be range) or starts a new fully-rounded single day.
                isSingleDaySelected && date > selectedStartDate!
                ? "right"
                : "neutral"
              : isInDepartureMode
                ? isBefore(date, selectedStartDate!)
                  ? "left"   // before arrival → would reset to new arrival
                  : "right"  // on/after arrival → picking departure
                : !selectedStartDate
                  ? "left"
                  : "neutral"
            : null;

          return (
            <div key={dayIdx} className="relative">
              <button
                type="button"
                onMouseEnter={() => onDateHover(date)}
                onClick={() => {
                  if (requireRange && onRangeChange) {
                    const { from, to } = singleDaySelect
                      ? applyActivityRangeSelect(date, selectedStartDate, selectedEndDate)
                      : applyRangeSelect(date, selectedStartDate, selectedEndDate);
                    onRangeChange(from, to);
                    return;
                  }
                  if (selectedStartDate && isSameDay(date, selectedStartDate)) {
                    onDateSelect(date);
                    return;
                  }
                  if (!isDisabled) {
                    onDateSelect(date);
                  }
                }}
                // `disabled` is the primary guard — browsers suppress click
                // events on disabled buttons before onClick can fire, so the
                // applyRangeSelect call in onClick is never a bypass.
                // Exception: arrival re-click must stay enabled so the user
                // can reset the selection by clicking the same date again.
                disabled={
                  isDisabled &&
                  !(selectedStartDate && isSameDay(date, selectedStartDate))
                }
                role="gridcell"
                className={clsx(
                  DAY_CELL_BOX,
                  "text-sm font-normal",
                  getDateButtonClasses(date, {
                    isPastDate,
                    isUnavailable,
                    stopTimeExpiredOnDate,
                    stopTimeBlocked,
                    stayRangeBlockedByStopTime,
                    disabledForDeparture,
                    isInDepartureMode,
                    isStartOrEnd: isStartOrEnd(date) ?? false,
                    isInRange: isInRange(date),
                    isSameMonth: isSameMonth(date, month),
                    isToday: isToday(date),
                    selectedStartDate,
                    selectedEndDate,
                    hoveredShape,
                  }),
                )}
                data-testid="calendar-day-button"
              >
                <time dateTime={format(date, "yyyy-MM-dd")}>
                  {format(date, "d")}
                </time>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const Calendar: React.FC<CalendarProps> = ({
  selectedStartDate,
  selectedEndDate,
  onDateSelect,
  currentMonth,
  onMonthChange,
  variant = "double",
  bookingAvailability,
  noShadow = false,
  bare = false,
  nested = false,
  requireRange,
  onRangeChange,
  singleDaySelect,
  labels,
  languageCode,
  isLoading = false,
  isRefreshing = false,
}) => {
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const handleDateHover = useCallback((date: Date | null) => setHoveredDate(date), []);

  const firstBlockedStayDate =
    selectedStartDate && selectedEndDate
      ? getFirstBlockedStayDateByStopTime(
          selectedStartDate,
          selectedEndDate,
          bookingAvailability,
        )
      : selectedStartDate &&
          isStopTimeExpired(selectedStartDate, bookingAvailability)
        ? selectedStartDate
        : null;

  const selectedStopTimeMessage =
    firstBlockedStayDate &&
    getStopTimeMessageForDate(
      firstBlockedStayDate,
      bookingAvailability,
      languageCode,
    );

  const showStopTimeMessage =
    !!firstBlockedStayDate && !!selectedStopTimeMessage;

  const weekDays = labels.weekdays;

  // Calculate the second month (next month) for double variant
  const nextMonth = addMonths(currentMonth, 1);

  // Both are rendered as the card's LAST children: the bar is positioned
  // absolutely and the status region is announced wherever it sits, while the
  // dialogs style this card's header through `> div > div:first-child` — a node
  // in front of the header steals that match and changes the card's height.
  // Always mounted, content toggled: several screen readers skip an
  // announcement when a live region is inserted already carrying its text.
  const loadingStatus = (
    <span role="status" className="sr-only">
      {isLoading ? labels.loadingAvailability : ""}
    </span>
  );

  const refreshBar = isRefreshing ? (
    <Skeleton
      className="absolute left-4 right-4 top-2 h-[3px] rounded-full !bg-ink/20"
      data-testid="calendar-availability-refreshing"
    />
  ) : null;

  if (variant === "single") {
    return (
      <div
        className={clsx(
          "relative w-full min-w-0 sm:min-w-[336px]",
          !bare && "border border-border bg-surface-card p-6",
          !bare && (nested ? "rounded-18" : "rounded-3xl"),
          !bare && !noShadow && !nested && "shadow-lg",
        )}
        onMouseLeave={() => handleDateHover(null)}
      >
        {/* Single Calendar Header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => onMonthChange(subMonths(currentMonth, 1))}
            className="rounded-full p-2 hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            type="button"
            data-testid="previous-month-button"
          >
            <FiChevronLeft className="h-5 w-5" />
          </button>
          <h2
            className="text-base font-semibold capitalize text-ink"
            data-testid="calendar-month-label"
          >
            {formatMonthHeader(currentMonth, languageCode)}
          </h2>
          <button
            onClick={() => onMonthChange(addMonths(currentMonth, 1))}
            className="rounded-full p-2 hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            type="button"
            data-testid="next-month-button"
          >
            <FiChevronRight className="h-5 w-5" />
          </button>
        </div>

        <MonthCalendar
          month={currentMonth}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          onDateSelect={onDateSelect}
          weekDays={weekDays}
          showHeader={false}
          isLoading={isLoading}
          bookingAvailability={bookingAvailability}
          requireRange={requireRange}
          onRangeChange={onRangeChange}
          singleDaySelect={singleDaySelect}
          hoveredDate={hoveredDate}
          onDateHover={handleDateHover}
          languageCode={languageCode}
        />
        {showStopTimeMessage && (
          <p className="mt-4 font-inter text-xs font-medium leading-none tracking-normal text-ink-secondary">
            • {labels.stopTimeStayDateNotice} {selectedStopTimeMessage}
          </p>
        )}
        {refreshBar}
        {loadingStatus}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "relative",
        !bare && "min-w-[672px]",
        !bare && "border border-border bg-surface-card p-6",
        !bare && (nested ? "rounded-18" : "rounded-3xl"),
        !bare && !noShadow && !nested && "shadow-lg",
      )}
    >
      {/* Double Calendar Header with Navigation */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => onMonthChange(subMonths(currentMonth, 1))}
          className="rounded-full p-2 hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          type="button"
          data-testid="previous-month-button"
        >
          <FiChevronLeft className="h-5 w-5" />
        </button>

        {/* The dot marks the seam between the two months, so it has to sit on the rule below
            it. Centring this group is not enough: the two labels are different widths, so the
            dot lands half that difference off — 6.6px for "Ankomst" against "Avresa". Giving
            each label an equal share of the row puts the dot itself on the centre line. */}
        <div className="flex flex-1 items-center gap-2">
          <span className="flex-1 text-right text-sm font-medium text-ink-secondary">
            {labels.arrival}
          </span>
          <span className="text-sm text-ink-secondary">•</span>
          <span className="flex-1 text-left text-sm font-medium text-ink-secondary">
            {labels.departure}
          </span>
        </div>

        <button
          onClick={() => onMonthChange(addMonths(currentMonth, 1))}
          className="rounded-full p-2 hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          type="button"
          data-testid="next-month-button"
        >
          <FiChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Double Calendar Layout */}
      <div className="flex gap-8" onMouseLeave={() => handleDateHover(null)}>
        <MonthCalendar
          month={currentMonth}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          onDateSelect={onDateSelect}
          weekDays={weekDays}
          bookingAvailability={bookingAvailability}
          requireRange={requireRange}
          onRangeChange={onRangeChange}
          singleDaySelect={singleDaySelect}
          hoveredDate={hoveredDate}
          onDateHover={handleDateHover}
          languageCode={languageCode}
          isLoading={isLoading}
        />

        <div className="w-px bg-border" />

        <MonthCalendar
          month={nextMonth}
          isLoading={isLoading}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          onDateSelect={onDateSelect}
          weekDays={weekDays}
          bookingAvailability={bookingAvailability}
          requireRange={requireRange}
          onRangeChange={onRangeChange}
          singleDaySelect={singleDaySelect}
          hoveredDate={hoveredDate}
          onDateHover={handleDateHover}
          languageCode={languageCode}
        />
      </div>
      {showStopTimeMessage && (
        <p className="mt-4 font-inter text-xs font-medium leading-none tracking-normal text-ink-secondary">
          • {labels.stopTimeStayDateNotice} {selectedStopTimeMessage}
        </p>
      )}
      {refreshBar}
      {loadingStatus}
    </div>
  );
};
